/* Setup builder: the picture panel. SHARED by both stores: theme/axis-v2/assets and theme/l2k-v2/assets hold
 * identical copies (edit both; scripts/check-builder-viz-sync.sh says if they drift).
 *
 * Evan, 2026-10-04, of kitepaddlesurf.com's Armstrong complete set: "I like how each change they make, shows the
 * new selection on the product photo", then "I want this feature to be amazing". There, only the front wing changes
 * the photo (each size is a variant with its own render); the mast, fuselage and tail come from an options app and
 * change the price only. Here EVERY part shows, from the photos the catalogue already has:
 *
 *   - a stage with the latest pick, large, crossfading as it changes (the shop thumbnail first, the sharp image as
 *     soon as it arrives, so a tap on a phone answers at once);
 *   - PREVIEW: on a computer, pointing at an option shows it on the stage before it is chosen, so sizes can be
 *     compared without picking and un-picking (touch screens skip this: a tap chooses);
 *   - four slots, front wing, rear wing, fuselage, mast, that fill with the chosen parts; the next one to choose is
 *     marked, a filled one brings its part back to the stage, an empty one scrolls to its step;
 *   - with all four chosen, the stage shows the whole setup together;
 *   - on a phone, a small bar at the bottom with the parts and the total once the panel has scrolled away.
 *
 * The builder owns the logic; this only draws. API: AxisBuildViz.create(opts) -> { el, dock, update(info),
 * preview(part, key), attach(node, part, key) }.
 */
(function () {
  'use strict';

  var SLOTS = [
    { key: 'front', label: 'Front wing' },
    { key: 'rear', label: 'Rear wing' },
    { key: 'fuse', label: 'Fuselage' },
    { key: 'mast', label: 'Mast' }
  ];

  function el(t, c, x) { var e = document.createElement(t); if (c) e.className = c; if (x != null) e.textContent = x; return e; }

  // Shopify CDN images carry their size in the name ("..._300x.jpg"); ask for the size the place needs.
  function sized(url, w) {
    if (!url) return url;
    if (/_(\d+)x(\d+)?(?=\.(?:jpe?g|png|webp|gif)(?:\?|$))/i.test(url)) return url.replace(/_(\d+)x(\d+)?(?=\.(?:jpe?g|png|webp|gif)(?:\?|$))/i, '_' + w + 'x');
    return url + (url.indexOf('?') < 0 ? '?' : '&') + 'width=' + w;
  }

  // A foil, drawn simply: base plate and mast, fuselage, front wing at the nose, rear wing at the tail. Used for the
  // empty stage and, one part lit at a time, for the empty slots.
  var FOIL = '' +
    '<svg viewBox="0 0 240 140" aria-hidden="true" focusable="false">' +
    '<g class="fbv-d fbv-d-mast"><rect x="100" y="10" width="44" height="7" rx="2"/><path d="M116 17 L128 17 L126.5 104 L117.5 104 Z"/></g>' +
    '<g class="fbv-d fbv-d-fuse"><rect x="38" y="102" width="176" height="9" rx="4.5"/></g>' +
    '<g class="fbv-d fbv-d-front"><path d="M6 112 C 28 99, 92 99, 116 112 C 92 122, 28 122, 6 112 Z"/></g>' +
    '<g class="fbv-d fbv-d-rear"><path d="M192 105 C 202 99, 226 99, 236 105 C 226 110, 202 110, 192 105 Z"/></g>' +
    '</svg>';

  // Where each part sits on the empty-stage render (AXIS's own SES 940, assembled; theme asset foil-build-empty.jpg,
  // 1200x900), in % of the image. Evan, 2026-10-04: "Can the schematic of the drawing of the foil be more axis specific Vs
  // a child's depiction of a foil?" The drawing stays only as the fallback when a theme has no image.
  var HOTS = [
    { key: 'mast', label: 'Mast', x: 51, y: 24 },
    { key: 'rear', label: 'Rear wing', x: 33, y: 49 },
    { key: 'fuse', label: 'Fuselage', x: 47, y: 62 },
    { key: 'front', label: 'Front wing', x: 58, y: 81 }
  ];

  function create(opts) {
    var label = opts.label || function (p) { return p.title; };
    // The catalogue's titles are long ("SURF SKINNY - 300/45 Carbon Rear Hydrofoil wing"); the slots, the phone bar
    // and the whole-setup view use the part's own name ("SURF SKINNY 300/45"). The stage caption keeps the full title.
    function short(p) {
      var t = label(p)
        .replace(/\s+(ultra high mod(ulus)?\s+)?(reinforced\s+)?(solid\s+)?carbon\s+(rear\s+|front\s+)?hydrofoil\s+wing\b/gi, '')
        .replace(/\s+carbon\s+(rear|front)\s+(hydrofoil\s+)?wing\b/gi, '')
        .replace(/\s+with zinc.*$/i, '')
        .replace(/\s+foil mast\s*&\s*base plate\b/gi, '')
        .replace(/\s+length\b/gi, '')
        .replace(/\s*-\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
      return t || label(p);
    }
    var priceNode = opts.priceNode;
    var canHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

    var state = {}, shownHandles = {}, last = null, focusKey = null, pv = null, pvKey = null, info = {};
    var leaveTimer = null;

    // ---------- the panel
    var root = el('aside', 'fbv');
    root.setAttribute('aria-label', 'Your foil');
    var stage = el('div', 'fbv-stage');
    var empty = el('div', 'fbv-empty' + (opts.emptyImage ? ' has-fig' : ''));
    if (opts.emptyImage) {
      var fig = el('div', 'fbv-fig');
      var fimg = el('img', 'fbv-fig-img');
      fimg.src = opts.emptyImage; fimg.alt = 'An AXIS foil: front wing, rear wing, fuselage and mast'; fimg.decoding = 'async';
      fig.appendChild(fimg);
      HOTS.forEach(function (h) {
        var m = el('span', 'fbv-hot');
        m.setAttribute('data-part', h.key);
        m.style.left = h.x + '%'; m.style.top = h.y + '%';
        m.appendChild(el('span', 'fbv-hot-dot'));
        m.appendChild(el('span', 'fbv-hot-lab', h.label));
        fig.appendChild(m);
      });
      empty.appendChild(fig);
    } else {
      empty.innerHTML = FOIL;
    }
    var emptyText = el('p', 'fbv-empty-text', opts.emptyImage ? 'Build your foil, part by part. Start with the front wing.' : 'Your foil appears here as you build it. Start with the front wing.');
    empty.appendChild(emptyText);
    var layers = [el('img', 'fbv-img'), el('img', 'fbv-img')];
    layers.forEach(function (im) { im.alt = ''; im.decoding = 'async'; stage.appendChild(im); });
    var grid = el('div', 'fbv-whole');           // the complete setup, all four together
    var tag = el('span', 'fbv-tag');
    stage.appendChild(empty);
    stage.appendChild(grid);
    stage.appendChild(tag);
    root.appendChild(stage);

    var cap = el('div', 'fbv-cap');
    var capCat = el('span', 'fbv-cap-cat'), capName = el('span', 'fbv-cap-name'), capPrice = el('span', 'fbv-cap-price');
    cap.appendChild(capCat); cap.appendChild(capName); cap.appendChild(capPrice);
    root.appendChild(cap);
    var live = el('p', 'fbv-sr');                  // what screen readers hear: choices, not previews
    live.setAttribute('aria-live', 'polite');
    root.appendChild(live);

    var slotsBox = el('div', 'fbv-slots');
    var slotEls = {};
    SLOTS.forEach(function (s) {
      var b = el('button', 'fbv-slot');
      b.type = 'button';
      b.setAttribute('data-key', s.key);
      var pic = el('span', 'fbv-slot-pic');
      var txt = el('span', 'fbv-slot-txt');
      txt.appendChild(el('span', 'fbv-slot-cat', s.label));
      txt.appendChild(el('span', 'fbv-slot-name', ''));
      b.appendChild(pic); b.appendChild(txt);
      b.addEventListener('click', function () {
        if (state[s.key]) { focusKey = s.key; pv = null; draw(); }
        else if (opts.onSlot) opts.onSlot(s.key);
      });
      slotEls[s.key] = b;
      slotsBox.appendChild(b);
    });
    root.appendChild(slotsBox);
    var totalBox = el('div', 'fbv-total');
    root.appendChild(totalBox);

    // ---------- the phone bar
    var dock = el('button', 'fbv-dock');
    dock.type = 'button';
    dock.setAttribute('aria-label', 'Show your foil');
    var dockPics = el('span', 'fbv-dock-pics'), dockTotal = el('span', 'fbv-dock-total');
    // On a phone the panel is above the steps, so a tap down in the list would change a picture nobody sees. The bar
    // answers instead: for a moment it opens on the part just added, then folds back to the four thumbnails.
    var flash = el('span', 'fbv-dock-flash'), flashImg = el('img'), flashTxt = el('span', 'fbv-dock-flash-txt');
    flashImg.alt = '';
    flash.appendChild(flashImg); flash.appendChild(flashTxt);
    dock.appendChild(flash); dock.appendChild(dockPics); dock.appendChild(dockTotal);
    var flashTimer = null;
    function flashPart(key, p) {
      if (panelVisible || !window.matchMedia || !window.matchMedia('(max-width: 989px)').matches) return;
      flashImg.src = sized(p.image, 300);
      flashTxt.innerHTML = '';
      flashTxt.appendChild(el('span', 'fbv-dock-lab', (SLOTS.filter(function (x) { return x.key === key; })[0].label) + ' added'));
      flashTxt.appendChild(el('strong', null, short(p)));
      dock.classList.add('is-flash');
      clearTimeout(flashTimer);
      flashTimer = setTimeout(function () { dock.classList.remove('is-flash'); }, 2600);
    }
    dock.addEventListener('click', function () { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    var panelVisible = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { panelVisible = es[0].isIntersecting; drawDock(); }, { threshold: 0.15 }).observe(stage);
    }

    // ---------- the stage picture: low-res at once, sharp as soon as it loads, crossfaded
    var active = 0, tok = 0;
    function put(src, low) {
      var next = layers[1 - active];
      next.src = src;
      next.classList.toggle('is-low', !!low);
      next.classList.add('is-on');
      layers[active].classList.remove('is-on');
      active = 1 - active;
    }
    function showPart(p) {
      var my = ++tok;
      var hi = sized(p.image, 900), lo = sized(p.image, 300);
      layers.forEach(function (im) { im.alt = label(p); });
      if (layers[active].classList.contains('is-on') && layers[active].src === hi) return;
      var img = new Image();
      var sharp = false;
      img.onload = function () { if (my === tok) { sharp = true; put(hi, false); } };
      img.src = hi;
      if (img.complete && img.naturalWidth) { sharp = true; put(hi, false); return; }
      setTimeout(function () { if (my === tok && !sharp) put(lo, true); }, 80);
    }
    function hideLayers() { tok++; layers.forEach(function (im) { im.classList.remove('is-on'); }); }

    function money(p) { return priceNode ? priceNode('fbv-price', p) : el('span', 'fbv-price', '$' + p.price); }

    function chosenKeys() { return SLOTS.filter(function (s) { return state[s.key]; }).map(function (s) { return s.key; }); }
    function nextKey() { var n = SLOTS.filter(function (s) { return !state[s.key]; })[0]; return n ? n.key : null; }

    function draw() {
      var keys = chosenKeys();
      var complete = keys.length === SLOTS.length;
      var showKey = pv ? pvKey : (focusKey && state[focusKey] ? focusKey : null);
      var part = pv || (showKey ? state[showKey] : null);
      var whole = !part && complete;
      if (!part && !whole && last && state[last]) { part = state[last]; showKey = last; }

      root.classList.toggle('is-empty', !part && !whole);
      root.classList.toggle('is-whole', whole);
      root.classList.toggle('is-preview', !!pv);
      cap.classList.toggle('is-whole', whole);
      empty.querySelectorAll('.fbv-hot').forEach(function (m) {
        var k = m.getAttribute('data-part');
        m.classList.toggle('is-set', !!state[k]);
        m.classList.toggle('is-next', k === nextKey());
      });
      empty.querySelectorAll('.fbv-d').forEach(function (g) {
        var k = (g.getAttribute('class').match(/fbv-d-(\w+)/) || [])[1];
        g.classList.toggle('is-set', !!state[k]);
        g.classList.toggle('is-next', k === nextKey());
      });

      grid.innerHTML = '';
      if (whole) {
        hideLayers();
        SLOTS.forEach(function (s) {
          var cell = el('button', 'fbv-whole-cell');
          cell.type = 'button';
          var im = el('img'); im.src = sized(state[s.key].image, 500); im.alt = label(state[s.key]); im.decoding = 'async';
          cell.appendChild(im);
          var cc = el('span', 'fbv-whole-cat');
          cc.appendChild(el('span', null, s.label));
          cc.appendChild(el('strong', null, short(state[s.key])));
          cell.appendChild(cc);
          cell.addEventListener('click', function () { focusKey = s.key; draw(); });
          grid.appendChild(cell);
        });
        tag.textContent = 'Your complete foil';
        capCat.textContent = 'Complete setup';
        capName.textContent = 'All four parts fit together. Tap one to see it large.';
        capPrice.innerHTML = '';
      } else if (part) {
        showPart(part);
        var slotLabel = (SLOTS.filter(function (s) { return s.key === showKey; })[0] || {}).label || '';
        tag.textContent = pv ? (canHover ? 'Preview: click to choose' : 'Preview') : (showKey === last && !focusKey ? 'Just added' : slotLabel);
        capCat.textContent = slotLabel;
        capName.textContent = label(part);
        capPrice.innerHTML = '';
        if (part.price) capPrice.appendChild(money(part));
      } else {
        hideLayers();
        tag.textContent = '';
        capCat.textContent = ''; capName.textContent = ''; capPrice.innerHTML = '';
      }

      // slots
      var nk = nextKey();
      SLOTS.forEach(function (s) {
        var b = slotEls[s.key], p = state[s.key];
        var ghost = pv && pvKey === s.key && !p;
        b.classList.toggle('is-filled', !!p);
        b.classList.toggle('is-next', !p && s.key === nk);
        b.classList.toggle('is-shown', !whole && !!part && showKey === s.key);
        b.classList.toggle('is-ghost', !!ghost);
        var pic = b.querySelector('.fbv-slot-pic');
        var shown = p || (ghost ? pv : null);
        var want = shown ? sized(shown.image, 160) : '';
        if (pic.getAttribute('data-src') !== want) {
          pic.setAttribute('data-src', want);
          pic.innerHTML = '';
          if (shown) { var im = el('img'); im.src = want; im.alt = ''; im.decoding = 'async'; pic.appendChild(im); }
          else if (opts.placeholders && opts.placeholders[s.key]) {
            // a real AXIS part of that kind, faded, stands in until one is chosen
            var ph = el('img', 'fbv-ph'); ph.src = sized(opts.placeholders[s.key], 160); ph.alt = ''; ph.decoding = 'async'; pic.appendChild(ph);
          } else { pic.innerHTML = FOIL; pic.querySelector('.fbv-d-' + s.key).classList.add('is-lit'); }
          if (p) { b.classList.remove('is-pop'); void b.offsetWidth; b.classList.add('is-pop'); }
        }
        b.querySelector('.fbv-slot-name').textContent = p ? short(p) : (ghost ? short(pv) : (s.key === nk ? 'Choose next' : ''));
        b.setAttribute('aria-label', s.label + (p ? ': ' + label(p) + '. Show it.' : (s.key === nk ? ': choose next.' : ': not chosen yet.')));
      });

      // total
      totalBox.innerHTML = '';
      if (keys.length) {
        var t = el('div', 'fbv-total-line');
        t.appendChild(el('span', 'fbv-total-lab', complete ? 'Complete setup' : keys.length + ' of 4 chosen'));
        if (info.total != null && priceNode) t.appendChild(priceNode('fbv-total-n', { price: info.total, compareAt: info.was }));
        totalBox.appendChild(t);
        if (info.extras && info.extras.length) {
          totalBox.appendChild(el('p', 'fbv-extras', 'Includes ' + info.extras.map(function (x) { return x.part.title.replace(/^AXIS( Foils)?\s+/i, ''); }).join(', ') + ', which your mast needs.'));
        }
        if (opts.onReview && complete) {
          // The builder names what the button does: "Add setup to cart" when it can add the whole setup at once,
          // "Review your setup" when a part needs its options chosen first (Evan, 2026-10-19: "I clicked Review and
          // add to cart, I ended up with this cart"; the button had only scrolled to the summary).
          var go = el('button', 'fbv-go', opts.reviewLabel ? opts.reviewLabel() : 'Review and add to cart');
          go.type = 'button';
          go.addEventListener('click', opts.onReview);
          totalBox.appendChild(go);
        }
      }
      drawDock();
    }

    function drawDock() {
      var keys = chosenKeys();
      var on = keys.length > 0 && !panelVisible;
      dock.classList.toggle('is-on', on);
      if (!on) return;
      dockPics.innerHTML = '';
      SLOTS.forEach(function (s) {
        var c = el('span', 'fbv-dock-pic' + (state[s.key] ? '' : ' is-empty'));
        if (state[s.key]) { var im = el('img'); im.src = sized(state[s.key].image, 100); im.alt = ''; c.appendChild(im); }
        dockPics.appendChild(c);
      });
      dockTotal.innerHTML = '';
      dockTotal.appendChild(el('span', 'fbv-dock-lab', keys.length === SLOTS.length ? 'Your foil' : keys.length + ' of 4'));
      if (info.total != null && priceNode) dockTotal.appendChild(priceNode('fbv-dock-n', { price: info.total, compareAt: info.was }));
    }

    return {
      el: root,
      dock: dock,
      /** After every builder render: info = { state, total, was, extras }. */
      update: function (i) {
        info = i || {};
        var s = info.state || {};
        var changed = null;
        SLOTS.forEach(function (x) {
          var h = s[x.key] ? s[x.key].handle : null;
          if (h && h !== shownHandles[x.key]) changed = x.key;
          shownHandles[x.key] = h;
        });
        state = {};
        SLOTS.forEach(function (x) { state[x.key] = s[x.key] || null; });
        if (changed) {
          last = changed; focusKey = null; pv = null;
          flashPart(changed, state[changed]);
          live.textContent = (SLOTS.filter(function (x) { return x.key === changed; })[0].label) + ' chosen: ' + label(state[changed]) + '.';
        }
        if (last && !state[last]) {
          var ks = chosenKeys();
          last = ks.length ? ks[ks.length - 1] : null;
        }
        if (focusKey && !state[focusKey]) focusKey = null;
        if (pv && pvKey && state[pvKey] && state[pvKey].handle === pv.handle) pv = null;
        draw();
      },
      /** Show a part on the stage before it is chosen; null puts the stage back. */
      preview: function (p, key) {
        clearTimeout(leaveTimer);
        if (p) { pv = p; pvKey = key; draw(); return; }
        // a short grace period, so sliding from one option to the next does not flash the chosen part in between
        leaveTimer = setTimeout(function () { pv = null; pvKey = null; draw(); }, 90);
      },
      /** Wire an option's element (a card, a row, a family button) to preview on pointer hover and keyboard focus. */
      attach: function (node, p, key) {
        var api = this;
        if (canHover) {
          node.addEventListener('mouseenter', function () { api.preview(p, key); });
          node.addEventListener('mouseleave', function () { api.preview(null); });
        }
        node.addEventListener('focus', function () { if (node.matches(':focus-visible') || node.getAttribute('role') === 'option') api.preview(p, key); });
        node.addEventListener('blur', function () { api.preview(null); });
      }
    };
  }

  // ---------------------------------------------------------------------------------------------------------------
  // THE PULL-DOWN (Evan, 2026-10-04, of the picture-card grid: "Each product category should be a pull down menu.
  // Otherwise the endless options are overwhelming"). One per step. Closed, it is a single row: the chosen part with its
  // thumbnail and price, or "Choose a rear wing" with how many fit. Open, a list grouped by family (headers that stay
  // in view, a strip of family shortcuts on top), each option with a small photo, its price and any fit or kit note.
  // Choosing again from the same menu changes the part; the builder re-checks the later steps as before.
  //   AxisBuildViz.dropdown({ key, placeholder, sub, chosen, groups: [{ name, items: [{ p, note, warn, suggested }] }],
  //     disabledText, next, label, priceNode, onChoose(p), viz })
  // Keyboard: Enter/Space/ArrowDown open; arrows move; Enter chooses; Escape closes. Hover or focus previews on the panel.
  var openDD = null;
  function closeOpen() { if (openDD) openDD(); }
  document.addEventListener('click', function (e) { if (openDD && !(e.target.closest && e.target.closest('.fbp.is-open'))) closeOpen(); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && openDD) closeOpen(); });

  function dropdown(o) {
    var label = o.label || function (p) { return p.title; };
    var root = el('div', 'fbp' + (o.disabledText ? ' is-disabled' : '') + (o.next ? ' is-next' : '') + (o.chosen ? ' is-chosen' : ''));
    var trig = el('button', 'fbp-trigger');
    trig.type = 'button';
    trig.setAttribute('aria-haspopup', 'listbox');
    trig.setAttribute('aria-expanded', 'false');
    if (o.chosen && o.chosen.image) { var ti = el('img', 'fbp-thumb'); ti.src = sized(o.chosen.image, 160); ti.alt = ''; trig.appendChild(ti); }
    var tt = el('span', 'fbp-tr-txt');
    tt.appendChild(el('span', 'fbp-tr-main', o.disabledText || (o.chosen ? label(o.chosen) : o.placeholder)));
    if (!o.disabledText) tt.appendChild(el('span', 'fbp-tr-sub', o.chosen ? 'Change' : (o.sub || '')));
    trig.appendChild(tt);
    if (o.chosen && o.chosen.price && o.priceNode) trig.appendChild(o.priceNode('fbp-tr-price', o.chosen));
    trig.appendChild(el('span', 'fbp-caret'));
    root.appendChild(trig);
    if (o.disabledText) { trig.disabled = true; return root; }

    var panel = el('div', 'fbp-panel');
    var list = el('div', 'fbp-list');
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', o.placeholder || '');
    var groups = o.groups || [];
    if (groups.length > 1) {
      var jump = el('div', 'fbp-jump');
      groups.forEach(function (g, gi) {
        var c = el('button', 'fbp-jump-chip' + (g.suggested ? ' is-suggested' : ''), g.name);
        c.type = 'button';
        c.addEventListener('click', function (e) {
          e.stopPropagation();
          var hd = list.querySelector('[data-g="' + gi + '"]');
          if (hd) list.scrollTop = hd.offsetTop - list.offsetTop;
        });
        jump.appendChild(c);
      });
      panel.appendChild(jump);
    }
    var opts = [];
    groups.forEach(function (g, gi) {
      var hd = el('div', 'fbp-group' + (g.suggested ? ' is-suggested' : ''));
      hd.setAttribute('data-g', String(gi));
      hd.appendChild(el('span', null, g.name));
      hd.appendChild(el('span', 'fbp-group-n', String(g.items.length)));
      list.appendChild(hd);
      g.items.forEach(function (it) {
        var isChosen = !!(o.chosen && o.chosen.handle === it.p.handle);
        var b = el('div', 'fbp-opt' + (it.suggested ? ' is-suggested' : '') + (isChosen ? ' is-chosen' : ''));
        b.setAttribute('role', 'option');
        b.setAttribute('tabindex', '-1');
        b.setAttribute('aria-selected', isChosen ? 'true' : 'false');
        var im = el('img'); im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
        if (it.p.image) im.src = sized(it.p.image, 160);
        b.appendChild(im);
        b.appendChild(el('span', 'fbp-opt-name', label(it.p)));
        if (it.p.price && o.priceNode) b.appendChild(o.priceNode('fbp-opt-price', it.p)); else b.appendChild(el('span'));
        if (it.note) b.appendChild(el('span', 'fbp-opt-note' + (it.warn ? ' fbp-warn' : ''), it.note));
        b.addEventListener('click', function (e) { e.stopPropagation(); close(); o.onChoose(it.p); });
        if (o.viz) o.viz.attach(b, it.p, o.key);
        list.appendChild(b);
        opts.push(b);
      });
    });
    panel.appendChild(list);
    root.appendChild(panel);

    function open() {
      if (openDD && openDD !== close) closeOpen();
      root.classList.add('is-open');
      trig.setAttribute('aria-expanded', 'true');
      openDD = close;
      // open on the chosen part, or the first suggested one, so it is in view
      var at = list.querySelector('.fbp-opt.is-chosen') || list.querySelector('.fbp-opt.is-suggested');
      if (at) list.scrollTop = Math.max(0, at.offsetTop - list.offsetTop - 48);
      // keep the open list on screen: if it would run off the bottom, bring the step up
      var r = panel.getBoundingClientRect();
      if (r.bottom > window.innerHeight) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    function close() {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      trig.setAttribute('aria-expanded', 'false');
      if (openDD === close) openDD = null;
      if (o.viz) o.viz.preview(null);
    }
    trig.addEventListener('click', function (e) { e.stopPropagation(); if (root.classList.contains('is-open')) close(); else open(); });
    trig.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); open();
        var first = list.querySelector('.fbp-opt.is-chosen') || opts[0];
        if (first) first.focus();
      }
    });
    list.addEventListener('keydown', function (e) {
      var i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (opts[Math.min(opts.length - 1, i + 1)] || opts[0]).focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (i <= 0) { trig.focus(); close(); } else opts[i - 1].focus(); }
      else if (e.key === 'Home') { e.preventDefault(); opts[0].focus(); }
      else if (e.key === 'End') { e.preventDefault(); opts[opts.length - 1].focus(); }
      else if ((e.key === 'Enter' || e.key === ' ') && i >= 0) { e.preventDefault(); opts[i].click(); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); trig.focus(); }
    });
    return root;
  }

  window.AxisBuildViz = { create: create, sized: sized, dropdown: dropdown };
})();
